/**
 * synced/total per-type view for the chip (#1950 Phase 2).
 *
 * Unifies the two halves of "is my work safe everywhere?":
 *  - synced  = the SERVER's authoritative event count for the rep's day
 *    (device-independent — GET /events/summary/). The server only holds acked
 *    events, so this is the confirmed count.
 *  - pending = the local, not-yet-acked events in the IDB event_queue.
 *  - total   = synced + pending → the chip shows `synced / total` (your 5/7).
 *
 * Offline / summary unavailable → `synced` is null and the UI degrades to
 * showing pending only. Each category counts ONE representative "item" event
 * type (task/day/drive events are activity, not items, so they'd inflate it).
 */

import { getAll, STORES } from './indexedDB';
import type { EventQueueRecord } from './eventLog';
import { eventsApi } from '../services/api';

export interface SyncCategory {
  key: string;
  label: string;
  synced: number | null; // null = server count unknown (offline)
  pending: number; // local, queued/in-flight, not yet acked
  failed: number; // local, terminal failure (event_queue sync_status 'error')
  total: number; // (synced ?? 0) + pending + failed
}

const CATEGORIES: { key: string; label: string; eventType: string }[] = [
  { key: 'visits', label: 'Visits', eventType: 'store_checked_in' },
  { key: 'surveys', label: 'Surveys', eventType: 'survey_submitted' },
  { key: 'photos', label: 'Photos', eventType: 'photo_uploaded' },
  { key: 'documents', label: 'Documents', eventType: 'document_uploaded' },
  { key: 'orders', label: 'Orders', eventType: 'order_created' },
  { key: 'inventory', label: 'Inventory', eventType: 'inventory_recorded' },
  { key: 'oos', label: 'Out of stock', eventType: 'oos_recorded' },
  { key: 'time', label: 'Time edits', eventType: 'time_edited' },
];

/** Pure combiner — server counts (null when offline) + local pending/failed counts. */
export function combineSyncStatus(
  serverCounts: Record<string, number> | null,
  localPending: Record<string, number>,
  localFailed: Record<string, number>,
): SyncCategory[] {
  const out: SyncCategory[] = [];
  for (const c of CATEGORIES) {
    const synced = serverCounts ? (serverCounts[c.eventType] ?? 0) : null;
    const pending = localPending[c.eventType] ?? 0;
    const failed = localFailed[c.eventType] ?? 0;
    const total = (synced ?? 0) + pending + failed;
    if (total === 0) continue; // nothing of this type today — omit
    out.push({ key: c.key, label: c.label, synced, pending, failed, total });
  }
  return out;
}

/** Split the local event queue into pending (queued/in-flight) vs failed (error). */
function tallyByType(rows: EventQueueRecord[]): {
  pending: Record<string, number>;
  failed: Record<string, number>;
} {
  const pending: Record<string, number> = {};
  const failed: Record<string, number> = {};
  for (const r of rows) {
    const bucket = r.sync_status === 'error' ? failed : pending;
    bucket[r.event_type] = (bucket[r.event_type] ?? 0) + 1;
  }
  return { pending, failed };
}

export async function getSyncStatusView(date: string, tz: string): Promise<SyncCategory[]> {
  let serverCounts: Record<string, number> | null = null;
  try {
    serverCounts = (await eventsApi.getSummary(date, tz)).counts ?? {};
  } catch {
    serverCounts = null; // offline / error — degrade to pending-only
  }
  let rows: EventQueueRecord[] = [];
  try {
    rows = (await getAll(STORES.EVENT_QUEUE)) as EventQueueRecord[];
  } catch {
    rows = [];
  }
  const { pending, failed } = tallyByType(rows);
  return combineSyncStatus(serverCounts, pending, failed);
}
